/**
 * Inline rich-text helpers for backend-supplied strings that may carry markup.
 *
 * ⚠️ READ THIS BEFORE REACHING FOR `sanitizeInlineHtml`.
 *
 * The standing rule for HTML-bearing backend fields is `plainText()`, never `v-html`
 * (docs/AGENT.md). That rule still holds for every BODY field — diagnosis, assessment,
 * note, plan, result, report. `sanitizeInlineHtml` exists for exactly ONE case:
 * `practitioner_name`, whose stored value colour-codes the honorific
 * (`<span style="color:blue">Dr.</span> Noor eldin`). It renders through
 * `RichTextInline.vue` and nowhere else.
 *
 * If that exception is ever widened to a rich-text BODY field, STOP and adopt DOMPurify.
 * The reasoning below is scoped to a short, inline-only fragment; a full document body has
 * an mXSS surface (foreign-content confusion in `<svg>`/`<math>`, `<template>`, `<noscript>`)
 * that this module does not attempt to cover — it sidesteps it by discarding those subtrees
 * outright, which is only acceptable because a name never legitimately contains them.
 *
 * Why a DOMParser walker rather than a regex, or DOMPurify:
 *  - Regex cannot repair unbalanced markup. `<span style="color:blue">Dr. Noor` with no
 *    closing tag would leak the colour into the rest of the table row. The HTML5 parser
 *    auto-closes at the fragment boundary and we re-serialize balanced.
 *  - DOMPurify is ~20 kB gzip for a name field, and it does NOT sanitize CSS declaration
 *    values under its default profile — we would ship the dependency and still write the
 *    property/value allow-list below.
 */

/* -------------------------------------------------------------------------------------- */
/* Allow-lists                                                                              */
/* -------------------------------------------------------------------------------------- */

/**
 * Inline-only. No `a` (an anchor nested inside the visit card's `RouterLink` is invalid
 * HTML and a phishing vector) and no `font` (the legacy presentational tag — supporting it
 * would re-admit the `color="blue"` attribute we deliberately do not honour).
 * No block tags: they would break the flex rows and table cells these names render into.
 */
export const RICH_TEXT_ALLOWED_TAGS: readonly string[] = [
  "span",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "small",
  "sub",
  "sup",
  "mark",
  "br",
];

/**
 * Tags whose ENTIRE SUBTREE is discarded rather than unwrapped, because their contents are
 * code or markup rather than prose. Every other disallowed tag is unwrapped (tag dropped,
 * children kept) so `<div>Noor</div>` still shows `Noor`.
 */
const DROP_SUBTREE_TAGS = new Set([
  "script",
  "style",
  "iframe",
  "object",
  "embed",
  "img",
  "svg",
  "math",
  "template",
  "noscript",
  "link",
  "meta",
  "base",
  "form",
  "input",
  "button",
  "select",
  "textarea",
  "audio",
  "video",
  "source",
  "canvas",
]);

/**
 * ⚠️ LONGHANDS ARE MANDATORY, not a style preference. Iterating `element.style` yields the
 * LONGHAND properties, so allow-listing only `text-decoration` would silently drop every
 * underline the clinic wrote.
 *
 * Excluded, each for a concrete reason:
 *  - `font-size`      — a name at `200px` blows up the table row and the phone card.
 *  - `display`, `position`, `inset`, `z-index`, `width`, `height`, `margin`, `padding`,
 *    `transform` — layout hijack / clickjacking overlay.
 *  - `background` (shorthand), `background-image` — pull in `url()`. `background-color` is
 *    the safe subset.
 *  - `opacity`, `visibility` — an invisible name in a clinical list is a data-integrity trap.
 *  - anything starting with `--` — custom properties accept an essentially arbitrary token
 *    stream, which is precisely why a PROPERTY allow-list (not a value grammar) rejects them.
 */
export const RICH_TEXT_ALLOWED_CSS_PROPERTIES: readonly string[] = [
  "color",
  "background-color",
  "font-weight",
  "font-style",
  "text-decoration-line",
  "text-decoration-color",
  "text-decoration-style",
];

const ALLOWED_TAGS = new Set(RICH_TEXT_ALLOWED_TAGS);
const ALLOWED_CSS_PROPERTIES = new Set(RICH_TEXT_ALLOWED_CSS_PROPERTIES);

/** `style` carries the colour. `dir`/`lang` let a clinic mark a mixed-script name. Nothing else — */
/** notably NO `class` (it would reach into our BEM and Vuetify utilities) and no `id`/`title`/`data-*`. */
const ALLOWED_ATTRIBUTES = new Set(["style", "dir", "lang"]);

const DIR_VALUES = new Set(["ltr", "rtl", "auto"]);
const LANG_PATTERN = /^[a-z]{1,8}(?:-[a-z0-9]{1,8})*$/i;

/** Only colour functions may appear in an allowed declaration value. */
const CSS_COLOR_FUNCTIONS = /^(?:rgb|rgba|hsl|hsla)$/;

/* -------------------------------------------------------------------------------------- */
/* Resource caps                                                                            */
/* -------------------------------------------------------------------------------------- */

/** A practitioner name. A payload past any of these is pathological, not data. */
const MAX_INPUT_LENGTH = 2000;
const MAX_ELEMENTS = 40;
const MAX_DEPTH = 8;
const MAX_STYLE_LENGTH = 500;
const MAX_CSS_VALUE_LENGTH = 120;

/* -------------------------------------------------------------------------------------- */
/* Plain text                                                                               */
/* -------------------------------------------------------------------------------------- */

/**
 * Cheap gate so the overwhelmingly common plain-name case never touches `DOMParser`.
 * `&` counts: an entity-only string still needs decoding before it is shown.
 */
export function hasMarkup(value: unknown): boolean {
  return /[<&]/.test(String(value ?? ""));
}

/**
 * ⚠️ Block boundaries must become whitespace BEFORE the parser sees them.
 *
 * The five hand-rolled `plainText()` copies this replaces used `replace(/<[^>]*>/g, " ")`,
 * so `<p>a</p><p>b</p>` produced `"a b"`. A naive `textContent` gives `"ab"` — a silent
 * regression on the diagnosis fields those copies handle. Appending a space after each
 * closing block tag (`$&` keeps the tag for the parser) makes this a strict superset of
 * the old behaviour, plus correct entity decoding.
 */
function padBlockBoundaries(raw: string): string {
  return raw.replace(/<(?:br|\/p|\/div|\/li|\/h[1-6]|\/tr|\/td|\/th|\/blockquote)\b[^>]*>/gi, "$& ");
}

function collapseWhitespace(text: string): string {
  return text.replace(/ /g, " ").replace(/\s+/g, " ").trim();
}

/** No-DOM fallback (Electron main, any node context). Entity coverage matches the copies replaced. */
function plainTextWithoutDom(raw: string): string {
  return collapseWhitespace(
    padBlockBoundaries(raw)
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/gi, " ")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, "\"")
      .replace(/&#39;|&apos;/gi, "'")
      // &amp; LAST, so `&amp;lt;` decodes to the literal `&lt;` rather than to `<`.
      .replace(/&amp;/gi, "&"),
  );
}

/**
 * Canonical tag-strip + entity-decode + whitespace-collapse. Always returns a string.
 *
 * This is the DEFAULT treatment for any backend HTML field. Use it everywhere a value lands
 * somewhere HTML cannot render — print, PDF, speech synthesis, an attribute, a translated
 * sentence's interpolation slot.
 */
export function plainText(value: unknown): string {
  const raw = String(value ?? "");

  if (!raw)
    return "";

  if (!hasMarkup(raw))
    return collapseWhitespace(raw);

  if (typeof DOMParser === "undefined")
    return plainTextWithoutDom(raw);

  try {
    const parsed = new DOMParser().parseFromString(padBlockBoundaries(raw), "text/html");

    return collapseWhitespace(parsed.body.textContent ?? "");
  }
  catch {
    return plainTextWithoutDom(raw);
  }
}

/** `plainText`, but `null` for empty — matches the shape the API mappers use. */
export function plainTextOrNull(value: unknown): string | null {
  return plainText(value) || null;
}

/* -------------------------------------------------------------------------------------- */
/* Inline HTML sanitizer                                                                    */
/* -------------------------------------------------------------------------------------- */

/**
 * Module-scope probe. NEVER inserted into the live document, so setting `cssText` on it
 * parses declarations without fetching anything or applying anything to the page.
 */
let cssProbe: HTMLElement | null = null;

function getCssProbe(): HTMLElement | null {
  if (typeof document === "undefined")
    return null;

  if (!cssProbe)
    cssProbe = document.createElement("span");

  return cssProbe;
}

/**
 * Defence in depth, applied AFTER CSSOM has normalized the value.
 *
 * The function-name loop is the load-bearing part: it rejects `url(`, `var(`, `env(`,
 * `attr(`, `image-set(` and a bare `(` (empty name fails `CSS_COLOR_FUNCTIONS`). Banning
 * `\` kills CSS escape sequences like `\75 rl(` — though CSSOM has already resolved those,
 * which is exactly why we validate the PARSED value rather than the raw attribute string.
 */
function isSafeCssValue(value: string): boolean {
  if (!value || value.length > MAX_CSS_VALUE_LENGTH)
    return false;

  // Escapes, string/attribute breakout, at-rules, declaration separators.
  if (/[\\<>"'{};@]/.test(value))
    return false;

  if (value.includes("/*") || value.includes("*/"))
    return false;

  for (const match of value.matchAll(/([a-z-]*)\(/gi)) {
    if (!CSS_COLOR_FUNCTIONS.test(match[1].toLowerCase()))
      return false;
  }

  return /^[\w\s,.%#()/+-]+$/.test(value);
}

/**
 * Read the declarations back through CSSOM instead of validating the raw string.
 *
 * The browser's CSS parser drops invalid declarations, strips `/* comments *\/`, lowercases
 * property names and resolves escapes (`\62 lue` -> `blue`) before we ever inspect them —
 * so a payload cannot hide behind syntax we would have to re-implement. `!important` is
 * dropped by simply not re-emitting the priority.
 */
function sanitizeStyle(raw: string): string {
  if (!raw || raw.length > MAX_STYLE_LENGTH)
    return "";

  const probe = getCssProbe();

  if (!probe)
    return "";

  probe.style.cssText = "";
  probe.style.cssText = raw;

  const declarations: string[] = [];

  for (let index = 0; index < probe.style.length; index++) {
    const property = probe.style[index];

    // Custom properties (`--x`) DO surface here, and are rejected by the allow-list.
    if (!ALLOWED_CSS_PROPERTIES.has(property))
      continue;

    const value = probe.style.getPropertyValue(property).trim();

    if (!isSafeCssValue(value))
      continue;

    declarations.push(`${property}:${value}`);
  }

  probe.style.cssText = "";

  return declarations.join(";");
}

function copyAllowedAttributes(source: Element, target: Element): void {
  for (const attribute of Array.from(source.attributes)) {
    const name = attribute.name.toLowerCase();

    /**
     * ⚠️ The allow-list below already excludes these — this is the belt to its braces, and
     * it documents WHY they matter. `DOMParser` output is inert (no browsing context: no
     * script execution, no fetches, `<img onerror>` never fires), BUT THAT GUARANTEE ENDS
     * AT THE SINK. `v-html` is `innerHTML` on a LIVE element, where `onerror`/`onload` fire.
     */
    if (name.startsWith("on"))
      continue;

    if (!ALLOWED_ATTRIBUTES.has(name))
      continue;

    const value = attribute.value;

    if (name === "style") {
      const style = sanitizeStyle(value);

      if (style)
        target.setAttribute("style", style);

      continue;
    }

    if (name === "dir") {
      const dir = value.trim().toLowerCase();

      if (DIR_VALUES.has(dir))
        target.setAttribute("dir", dir);

      continue;
    }

    if (name === "lang" && LANG_PATTERN.test(value.trim()))
      target.setAttribute("lang", value.trim());
  }
}

/** Thrown to abort the walk when a cap trips; the caller falls back to plain text. */
class RichTextLimitError extends Error {}

function walk(source: Node, target: Node, depth: number, budget: { elements: number }): void {
  if (depth > MAX_DEPTH)
    throw new RichTextLimitError();

  const ownerDocument = target.ownerDocument ?? document;

  for (const child of Array.from(source.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      target.appendChild(ownerDocument.createTextNode(child.textContent ?? ""));
      continue;
    }

    if (child.nodeType !== Node.ELEMENT_NODE)
      continue;

    const element = child as Element;
    const tag = element.tagName.toLowerCase();

    // Contents are code or markup, not prose — drop the whole subtree rather than unwrap it.
    if (DROP_SUBTREE_TAGS.has(tag))
      continue;

    if (!ALLOWED_TAGS.has(tag)) {
      // Unwrap: keep the text a user would expect to read, lose the tag.
      walk(element, target, depth + 1, budget);
      continue;
    }

    if (++budget.elements > MAX_ELEMENTS)
      throw new RichTextLimitError();

    const clean = ownerDocument.createElement(tag);

    copyAllowedAttributes(element, clean);
    target.appendChild(clean);

    if (tag !== "br")
      walk(element, clean, depth + 1, budget);
  }
}

/** Escape via the DOM serializer, so there is no hand-rolled escaping to get wrong. */
function escapeViaDom(text: string): string {
  if (typeof document === "undefined") {
    return text
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  const holder = document.createElement("span");

  holder.textContent = text;

  return holder.innerHTML;
}

/**
 * Sanitize a short inline fragment to something safe for `v-html`.
 *
 * ⚠️ ONLY `RichTextInline.vue` should call this. See the module header.
 *
 * Returns escaped plain text (never raw markup) whenever the input is unparseable, exceeds
 * a resource cap, or there is no DOM — so every failure mode degrades to something inert
 * and readable rather than to nothing or to the original string.
 */
export function sanitizeInlineHtml(value: unknown): string {
  const raw = String(value ?? "");

  if (!raw)
    return "";

  if (!hasMarkup(raw))
    return escapeViaDom(collapseWhitespace(raw));

  if (typeof DOMParser === "undefined" || typeof document === "undefined")
    return escapeViaDom(plainText(raw));

  if (raw.length > MAX_INPUT_LENGTH)
    return escapeViaDom(plainText(raw));

  try {
    const parsed = new DOMParser().parseFromString(raw, "text/html");

    /**
     * Build a NEW detached tree and return its `innerHTML` rather than concatenating tag
     * strings. The browser's serializer handles text and attribute-value escaping, which
     * removes that entire class of bug. Nothing built here ever enters the live document.
     */
    const clean = document.createElement("span");

    walk(parsed.body, clean, 0, { elements: 0 });

    return clean.innerHTML;
  }
  catch {
    return escapeViaDom(plainText(raw));
  }
}
